import React from 'react';
import styles from './BusinessPackages.module.scss';
import { Button } from '../../../components/Button/Button';
import { scrollToBusinessInquiryForm } from '../../../components/Header/BusinessSolutionsHeader';
import checkDaylight from '../../../images/business-solutions-check-daylight.png';
import checkOrange from '../../../images/business-solutions-check-orange.png';
import checkOcean from '../../../images/business-solutions-check-ocean.png';

type Tier = {
  plan: 'Business 1 Gig' | 'Business 2 Gig' | 'Business 5 Gig';
  accent: 'daylight-blue' | 'orange' | 'ocean-blue';
  checkIcon: string;
  speed: string;
  tagline: string;
  bestValue?: boolean;
  features: { title: string; description: string }[];
};

const tiers: Tier[] = [
  {
    plan: 'Business 1 Gig',
    accent: 'daylight-blue',
    checkIcon: checkDaylight,
    speed: '1,000 MBPS DOWN / 1,000 MBPS UP',
    tagline: 'A strong starting point for everyday business.',
    features: [
      { title: 'Reliable Everyday Speed', description: 'Keep your business running smoothly.' },
      { title: 'Stay Productive', description: 'Keep calls, apps, and devices connected.' },
      { title: 'Room to Grow', description: 'More capacity as your business expands.' },
    ],
  },
  {
    plan: 'Business 2 Gig',
    accent: 'orange',
    checkIcon: checkOrange,
    speed: '2,000 MBPS DOWN / 2,000 MBPS UP',
    tagline: 'Built for growing teams and heavier daily use.',
    bestValue: true,
    features: [
      { title: 'Keep Things Moving', description: 'More bandwidth for more users.' },
      { title: 'Better Cloud Performance', description: 'Faster apps, smoother work.' },
      { title: 'More Capacity For Daily Work', description: 'Handle more without slowdowns.' },
    ],
  },
  {
    plan: 'Business 5 Gig',
    accent: 'ocean-blue',
    checkIcon: checkOcean,
    speed: '5,000 MBPS DOWN / 5,000 MBPS UP',
    tagline: 'Maximum performance for high-traffic business needs.',
    features: [
      { title: 'Built for High-Demand Operations', description: 'Power your most important work.' },
      { title: 'Power AI & Large Data Transfers', description: 'Move more data, faster.' },
      { title: 'Ready to Scale with your Business', description: 'Future-proof your connection.' },
    ],
  },
];

export const BusinessPackages = (): React.JSX.Element => {
  return (
    <section id="packages" className={styles.packages}>
      <div className="grid-12">
        <div className={styles.container}>
          <h2 className={`${styles.headline} color--marina-blue`}>
            Choose the Speed that Matches Your Workday.
          </h2>

          <div className={styles.grid}>
            {tiers.map((tier) => (
              <div key={tier.plan} className={styles.card} data-plan={tier.accent}>
                {tier.bestValue && <div className={styles.bestValueBar}>Best Value</div>}

                <div className={styles.cardFrame}>
                  <div className={styles.cardBody}>
                    <div className={styles.cardHeader}>
                      <h3>{tier.plan.toUpperCase()}</h3>
                      <p className={styles.speed}>{tier.speed}</p>
                    </div>

                    <div className={styles.tagline}>{tier.tagline}</div>

                    <ul className={styles.features}>
                      {tier.features.map((feature) => (
                        <li key={feature.title}>
                          <img src={tier.checkIcon} alt="" width="22" height="22" loading="lazy" />
                          <div>
                            <p className={styles.featureTitle}>{feature.title}</p>
                            <p className={styles.featureDescription}>{feature.description}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <Button
                  size="md"
                  onClick={scrollToBusinessInquiryForm}
                  className={`${styles.inquireBtn} bg-color--${tier.accent}`}
                >
                  Inquire Today
                </Button>
              </div>
            ))}
          </div>

          <div className={styles.moreBanner}>
            <div className={styles.moreBannerInner}>
              <p>
                <strong>Need more?</strong> Up to 10 Gig available for the right business needs.
              </p>
              <Button
                size="md"
                onClick={scrollToBusinessInquiryForm}
                className={`${styles.moreBannerBtn} bg-color--marina-blue`}
              >
                Inquire Today
              </Button>
            </div>
          </div>

          <p className={styles.disclaimer}>
            Service availability, final speed recommendations, equipment, pricing, and installation
            requirements are confirmed after an address review and free site walk.
          </p>
        </div>
      </div>
    </section>
  );
};
